// lodash
import get from 'lodash/get';
import includes from 'lodash/includes';

import { push } from 'connected-react-router';
import routeConstants from '../../../constants/routeConstants';
import errorCodeConstants from '../../../constants/errorCodeConstants';
import deliveryConstants from '../../../constants/deliveryConstants';

import { triggerAnalyticsEvent } from '../analytics/analyticsAction';
import {
  ANALYTICS_DELIVERY_OPTIONS,
  ANALYTICS_SUBMIT_ORDER_ERROR,
  APPLE_PAY_EXPRESS_ENABLED,
  APPLE_PAY_SELECTED,
  GET_PAYMENT_PAGE,
  GOOGLE_PAY_DEFAULT,
  GOOGLE_PAY_ENABLED,
  INIT_PAYMENT_PAGE,
  RESERVE_STOCK,
  REVERT_STOCK_RESERVATION,
  VOUCHER_UNDERSPEND_WARNING_MODAL_CLOSE,
} from '../../../constants/actionConstants';
import {
  URL_PAYMENT_PAGE,
  URL_STOCK_RESERVATION,
} from '../../../constants/endpointConstants';
import { canMakeGooglePayPayments } from './googlePayActions';
import { fetchPaymentWallet } from '../user/userActions';
import {
  getDeliveryMethodsAnalytics,
} from '../delivery/deliveryActions';
import { selectClickAndCollectStoreAnalytics } from '../../../utils/analytics/utils/getCollectionAnalytics';
import handleInconsistentOrderFormState from '../../../utils/orderform/handleInconsistentOrderFormState';
import paymentTypeConstants from '../../../constants/paymentTypeConstants';
import featureConstants from '../../../constants/featureConstants';
import { getPayPalClientToken } from './payPalPaymentActions';
import { isFeatureActive } from '../../reducers/config/configReducer';
import { isApps, isPactApp } from '../../reducers/app/appReducer';

const dispatch3rdPartyPaymentAnalytics = (
  customerCanMakeGooglePayPayments,
  expressApplePayFeatureEnabled,
) => async (dispatch, getState) => {
  // `payment.selectedPaymentType` should be taken from actual state after success InitPaymentPage
  const selectedPaymentType = get(getState(), 'payment.selectedPaymentType');
  const applePaySelected = includes(selectedPaymentType, paymentTypeConstants.APPLE_PAY);
  if (applePaySelected) {
    dispatch(triggerAnalyticsEvent(APPLE_PAY_SELECTED));
  }

  if (expressApplePayFeatureEnabled && applePaySelected) {
    dispatch(triggerAnalyticsEvent(APPLE_PAY_EXPRESS_ENABLED));
  }

  if (customerCanMakeGooglePayPayments) {
    dispatch(triggerAnalyticsEvent(GOOGLE_PAY_ENABLED));
    if (includes(selectedPaymentType, paymentTypeConstants.GOOGLE_PAY)) {
      dispatch(triggerAnalyticsEvent(GOOGLE_PAY_DEFAULT));
    }
  }
};

export const initPaymentPage = (getOnly, eventDetails, pushAfter) => async (dispatch, getState) => {
  const {
    app,
    clickAndCollect,
    orderForm,
    delivery,
    payment,
  } = getState();

  if (payment?.getPaymentPageCallActive) {
    // try to stop mysterious duplicate calls to payment-page
    return {};
  }

  const selectedCollectionPoint = get(clickAndCollect, 'selectedCollectionPoint');
  const deliveries = get(delivery, 'deliveries', []);
  const confirmedDeliveryAddress = get(delivery, 'confirmedDeliveryAddress');
  const selectedDeliveryChoiceId = get(delivery, 'selectedDeliveryChoiceId');
  const orderNeedsToBeRebatched = get(orderForm, 'orderNeedsToBeRebatched');

  const disableInitPaymentPage = orderNeedsToBeRebatched ||
    get(app, 'orderComplete', false) ||
    get(app, 'orderProcessing', false);

  if (disableInitPaymentPage) {
    return {};
  }

  const notReadyForPayment = (
    (selectedDeliveryChoiceId === deliveryConstants.DELIVERY && !confirmedDeliveryAddress) ||
    (selectedDeliveryChoiceId === deliveryConstants.CLICK_AND_COLLECT && !selectedCollectionPoint) ||
    !selectedDeliveryChoiceId
  );

  if (notReadyForPayment) {
    const pathname = selectedDeliveryChoiceId === deliveryConstants.CLICK_AND_COLLECT ?
      routeConstants.CLICK_AND_COLLECT : routeConstants.DELIVERY;
    dispatch(push({ pathname }));
    return {};
  }

  if (selectedDeliveryChoiceId === deliveryConstants.DELIVERY) {
    dispatch(getDeliveryMethodsAnalytics(get(delivery, 'deliveries')));
  }

  if (selectedDeliveryChoiceId === deliveryConstants.CLICK_AND_COLLECT) {

    const searchTerm = get(clickAndCollect, 'collectionPointSearchTerm');
    const collectionDates = get(clickAndCollect, 'collectionPointDates');
    const analytics = selectClickAndCollectStoreAnalytics(
      selectedCollectionPoint,
      searchTerm,
      collectionDates,
    );

    dispatch(triggerAnalyticsEvent(ANALYTICS_DELIVERY_OPTIONS, {
      analytics,
    }));
  }

  const customerCanMakeGooglePayPayments = getState().payment?.canMakeGooglePayPayments;
  if (customerCanMakeGooglePayPayments) {
    await dispatch(canMakeGooglePayPayments());
  }

  const expressApplePayFeatureEnabled =
    !isApps(getState()) &&
    isFeatureActive(getState(), featureConstants.EXPRESS_APPLE_PAY);

  const streamlinedPaymentsFeatureEnabled = isFeatureActive(getState(), featureConstants.STREAMLINED_PAYMENTS);

  const dispatchObject = {
    type: getOnly ? GET_PAYMENT_PAGE : INIT_PAYMENT_PAGE,
    request: client => client({
      path: URL_PAYMENT_PAGE,
      config: { method: getOnly ? 'GET' : 'POST' },
    }),
    selectedCollectionPoint,
    deliveries,
    expressApplePayFeatureEnabled,
    streamlinedPaymentsFeatureEnabled,
    ...pushAfter && { pushAfter },
  };

  let response;
  if (eventDetails) {
    response = await dispatch({
      ...dispatchObject,
      eventDetails,
    });
  } else {
    response = await dispatch(dispatchObject);
  }

  if (response.type === `${INIT_PAYMENT_PAGE}.SUCCESS`) {
    dispatch(fetchPaymentWallet());
    const isPayPalDisabled = isFeatureActive(getState(), featureConstants.DISABLE_PAYPAL) || isPactApp(getState());

    if (!isPayPalDisabled && payment && !payment.payPalClientToken) {
      const showError = false;
      dispatch(getPayPalClientToken(showError));
    }
  }

  if (!pushAfter) {
    dispatch(dispatch3rdPartyPaymentAnalytics(customerCanMakeGooglePayPayments, expressApplePayFeatureEnabled));
  }

  const error = get(response, 'error.code', '');
  if (error === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE) {
    await dispatch(handleInconsistentOrderFormState());
    return {};
  }

  if (pushAfter) {
    dispatch(push(routeConstants.PAYMENT));
    dispatch(dispatch3rdPartyPaymentAnalytics(customerCanMakeGooglePayPayments, expressApplePayFeatureEnabled));
  }

  return response;
};

export const getPaymentPage = eventDetails => (initPaymentPage(true, eventDetails));

export const getPaymentPageAfterSubmitOrderError = submitOrderAction => async (dispatch) => {
  const response = await dispatch(initPaymentPage(true, {
    posCreditSubmitOrderError: includes(submitOrderAction.selectedPaymentType, paymentTypeConstants.POS_CREDIT),
  }));

  if (response.type === `${GET_PAYMENT_PAGE}.SUCCESS`) {
    dispatch(triggerAnalyticsEvent(ANALYTICS_SUBMIT_ORDER_ERROR, { error: submitOrderAction.error }));
  }
};

export const reserveStock = () => ({
  type: RESERVE_STOCK,
  request: client => client({ path: URL_STOCK_RESERVATION, config: { method: 'POST' } }),
});

export const revertStockReservation = () => ({
  type: REVERT_STOCK_RESERVATION,
  request: client => client({ path: URL_STOCK_RESERVATION, config: { method: 'DELETE' } }),
});

export const closeVoucherUnderspendWarningModal = () => ({
  type: VOUCHER_UNDERSPEND_WARNING_MODAL_CLOSE,
});
